<template>
  <div class="sub-nav d-flex justify-content-between">
    <div class="d-flex">
      <property-tenure-select class="mr-4"></property-tenure-select>
      <scrollable-widget
          arrow-bg-color="var(--secondary)"
          arrow-fg-color="var(--gray-dark)"
          class="has-actions"
      >
        <b-navbar v-if="hasAccess">
          <b-navbar-nav>
            <b-nav-item
                class="nav-item"
                v-for="item in statuses"
                :key="item.status"
                :to="item.route"
                v-if="hasPermissionToFilterStatus(item)"
                exact
                exact-active-class="active"
            >
              {{ item.label }}
              <status-counter
                  v-if="item.status"
                  param="clientActionCount"
                  :bg-color="item.statusColor"
                  :status="item.status"
                  :count="clientActionCount(item.status.toLowerCase())"
              />
            </b-nav-item>
          </b-navbar-nav>
        </b-navbar>
      </scrollable-widget>
    </div>
    <b-navbar v-if="hasAccess" class="ml-auto py-1 px-3" ref="actionsMenu">
      <portal-target name="actions-subnav"></portal-target>
    </b-navbar>
  </div>
</template>

<script>
import StatusCounter from '@/components/Action/StatusCounter';
import ActionStatus from '@/services/Action/ActionStatus';
import ClientAction from '@/services/Action/ClientAction';
import ScrollableWidget from '@/components/Widgets/ScrollableWidget';
import PropertyTenureSelect from '@/components/Nav/PropertyTenureSelect';
import ActionsMenuVisibilityMixin from '@/components/Nav/ActionsMenuVisibilityMixin';
import {
  ACTIONS_ADD_CLIENT_ACTIONS,
  ACTIONS_APPROVE,
  ACTIONS_ASSIGN, ACTIONS_BULK_APPROVE_SUBMITTED, ACTIONS_REJECT,
  ACTIONS_VIEW_ALL, ACTIONS_VIEW_TEAM_ACTIONS,
  PERMISSION_TYPE_ACTION
} from '@/config/permission';

export default {
  name: 'Client-Action-Navbar',
  components: {
    ClientAction,
    StatusCounter,
    ScrollableWidget,
    PropertyTenureSelect,
  },
  mixins: [ActionsMenuVisibilityMixin],
  data() {
    return {
      statuses: [
        {
          label: 'Approved',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'approved'
            }
          },
          status: ActionStatus.APPROVED(),
          count: null,
          isBasicAccess: true,
          statusColor: '#2A2638',
        },
        {
          label: 'Open',
          route: {
            name: 'clientActions',
            params: {
              statusFilter: 'open'
            }
          },
          isBasicAccess: true,
          count: null,
          status: 'Open',
          statusColor: '#07792d',
        },
        {
          label: 'Unassigned',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'unassigned'
            }
          },
          status: ActionStatus.UNASSIGNED(),
          count: null,
          statusColor: '#638BC2',
          isBasicAccess: false,
        },
        {
          label: 'Assigned',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'assigned'
            }
          },
          status: ActionStatus.ASSIGNED(),
          count: null,
          statusColor: '#638BC2',
          isBasicAccess: true,
        },
        {
          label: 'My pending approval',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'approvals'
            },
          },
          status: ActionStatus.PENDING_APPROVAL(),
          statusColor: '#FFA600',
          isBasicAccess: false,
        },
        {
          label: 'Partially complete',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: ActionStatus.PARTIAL(),
            },
          },
          status: ActionStatus.PARTIAL(),
          statusColor: '#07792d',
          isBasicAccess: true,
          count: null,
        },
        {
          label: 'Rejected',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'rejected'
            },
          },
          status: ActionStatus.REJECTED(),
          statusColor: '#9d4983',
          isBasicAccess: true,
          count: null,
        },
        {
          label: 'Queried',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'query'
            }
          },
          status: ActionStatus.QUERY(),
          count: null,
          statusColor: '#00D2B4',
          isBasicAccess: true,
        },
        {
          label: 'Responded to query',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'query-response'
            },
          },
          status: ActionStatus.QUERY_RESPONSE(),
          statusColor: '#0031d2',
          isBasicAccess: true,
          count: null,
        },
        {
          label: 'No Access',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'no-access'
            }
          },
          status: ActionStatus.NO_ACCESS(),
          count: null,
          statusColor: '#C388BB',
          isBasicAccess: false,
        },
        {
          label: 'Responded to no access',
          route: {
            name: 'clientActionStatus',
            params: {
              statusFilter: 'no-access-response',
            },
          },
          status: ActionStatus.NO_ACCESS_RESPONSE(),
          statusColor: '#5049a6',
          count: null,
          isBasicAccess: true,
        },
      ],
    };
  },
  computed: {
    hasAccess() {
      return true;
    },
    clientActionCount() {
      return this.$store.getters['clientActionCount']
    }
  },
  mounted() {
    if (this.hasAccess) {
      this.$store.dispatch('refreshClientActionCounts');
    }
  },
  methods: {
    hasPermissionToFilterStatus(item) {
      if (item.isBasicAccess) {
        return true;
      }

      const canViewAll = this.$can(ACTIONS_VIEW_ALL, PERMISSION_TYPE_ACTION);
      const isTeamManager = this.$can(ACTIONS_VIEW_TEAM_ACTIONS, PERMISSION_TYPE_ACTION);

      const canApproveAndReject = this.$can(ACTIONS_ASSIGN, PERMISSION_TYPE_ACTION) &&
          this.$can(ACTIONS_APPROVE, PERMISSION_TYPE_ACTION) &&
          this.$can(ACTIONS_BULK_APPROVE_SUBMITTED, PERMISSION_TYPE_ACTION) &&
          this.$can(ACTIONS_REJECT, PERMISSION_TYPE_ACTION);

      const canAssign = this.$can(ACTIONS_ADD_CLIENT_ACTIONS, PERMISSION_TYPE_ACTION);

      const status = item.status ? item.status.toLowerCase() : null;

      if (status === 'pending approval') {
        return (canViewAll || isTeamManager) && canApproveAndReject;
      }

      if (canViewAll) {
        return true;
      }

      if (status === 'no access' && isTeamManager) {
        return true;
      }

      if (status === 'unassigned' && isTeamManager) {
        return canApproveAndReject && canAssign;
      }

      return false;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
.navbar-secondary {
  .badge {
    vertical-align: text-top;
  }
}
</style>
